import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartDataset,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { isEqual } from "lodash";
import { numericalString } from "../../../types";
import { GapHistoryEntry } from "../PitBoostCorrectedLeaderboard/types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
);

// Set global font defaults
ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

const colorWithOpacity = (color: string, opacity: number): string => {
  // Parse rgb(r, g, b) format
  const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (match) {
    return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${opacity})`;
  }
  return color;
};

interface OppositePitStrategyGraphProps {
  referenceDriver: string;
}

function OppositePitStrategyGraph({
  referenceDriver,
}: OppositePitStrategyGraphProps) {
  const participants = useSelector(
    (state: RootState) => state.virtualGaps.data.participants,
    isEqual,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const loopProportions = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data?.rLoopTimeVsLoopNameY,
    isEqual,
  );

  const timingLoops = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.track?.info?.timingLoops,
    isEqual,
  );

  // Build a map from loop index to loop name
  const loopNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (timingLoops) {
      Object.keys(timingLoops).forEach((loopIdx) => {
        const idx = parseInt(loopIdx, 10);
        if (!isNaN(idx)) {
          const loopInfo = timingLoops[loopIdx as `${number}`];
          map[idx] = loopInfo?.name || `L${loopIdx}`;
        }
      });
    }
    return map;
  }, [timingLoops]);

  const chartData = useMemo(() => {
    const getXValue = (lap: number, loop: number) => {
      const proportion = loopProportions?.[loop] ?? loop / 8;
      return lap + proportion;
    };

    if (
      !referenceDriver ||
      !participants ||
      Object.keys(participants).length === 0 ||
      !participants[referenceDriver as numericalString]
    ) {
      return {
        labels: [],
        datasets: [],
        xMin: 0,
        xMax: 1,
        tickValues: [],
      };
    }

    const referenceHistory =
      participants[referenceDriver as numericalString]?.gapHistoryPitWindow;
    if (!referenceHistory || referenceHistory.length === 0) {
      return {
        labels: [],
        datasets: [],
        xMin: 0,
        xMax: 1,
        tickValues: [],
      };
    }

    const referenceGapHistory = (() => {
      if (!referenceHistory) return {};
      const vgParticipant = participants?.[referenceDriver as numericalString];
      if (!vgParticipant) return {};
      const history = vgParticipant.gapHistoryPitWindow;
      if (!history) return {};
      const gapRecord: Record<number, Record<number, number>> = {};
      for (const entry of history) {
        gapRecord[entry.lap] ??= {};
        if (
          entry.loop !== undefined &&
          entry.gapToRefPitCorrected !== undefined
        ) {
          gapRecord[entry.lap][entry.loop] ??= entry.gapToRefPitCorrected;
        }
      }
      return gapRecord;
    })();

    // Get reference driver's pit status (only consider pitted if boostStopLap is set)
    const referenceBoostStopLap =
      participants[referenceDriver as numericalString]?.boostStopLap;

    const pitInLoop = loopProportions ? loopProportions.length - 1 : 7;

    const referenceHasPitted =
      (participants[referenceDriver as numericalString]?.hasPitBoosted ||
        false) &&
      referenceBoostStopLap != null;

    const pitStopMarkers: ChartDataset<"line">[] = [];
    const attackTraces: ChartDataset<"line">[] = [];
    const predictionLines: ChartDataset<"line">[] = [];

    // Calculate xMax early for prediction lines
    const allCarXValues = Object.values(participants).flatMap(
      (p) =>
        p?.gapHistoryPitWindow?.map((entry: GapHistoryEntry) =>
          getXValue(entry.lap, entry.loop),
        ) ?? [],
    );
    const calculatedXMax =
      allCarXValues.length > 0 ? Math.max(...allCarXValues) : 1;

    const datasets = Object.keys(participants)
      .filter((driverKey) => {
        if (driverKey === referenceDriver) return false;
        if (!referenceHasPitted) {
          // Reference hasn't pitted, only show drivers who have pitted
          return (
            participants[driverKey as numericalString]?.hasPitBoosted || false
          );
        }
        // Reference has pitted,only show drivers who haven't pitted
        return !participants[driverKey as numericalString]?.hasPitBoosted;
      })
      .map((driverKey) => {
        const driverData = participants[driverKey as numericalString];
        const driverHistory = driverData?.gapHistoryPitWindow;
        const driverBoostStopLap = driverData?.boostStopLap;

        const driverInfoEntry =
          driverInfo[driverKey as keyof typeof driverInfo];
        const driverName = driverInfoEntry?.driverName || driverKey;
        const color = driverInfoEntry?.driverTeamColour || "white";
        const lineStyle = driverInfoEntry?.driverLineStyle || "solid";
        const borderDash = lineStyle === "solid" ? [] : [10, 5];

        const dataPoints: { x: number; y: number }[] = [];
        const attackActivations: { x: number; y: number }[][] = [];
        let currentAttackActivation: { x: number; y: number }[] = [];
        let wasInAttack = false;
        let pitStopPoint: { x: number; y: number } | null = null;

        if (driverHistory && driverHistory.length > 0) {
          for (const entry of driverHistory) {
            const refEntry = referenceGapHistory?.[entry.lap]?.[entry.loop];

            if (refEntry !== undefined) {
              const currentDelta = entry.gapToRefPitCorrected - refEntry;
              const deltaToPlot = -currentDelta;
              const xValue = getXValue(entry.lap, entry.loop);
              const yValue = Math.round(deltaToPlot * 100) / 100;

              let shouldInclude = false;

              if (!referenceHasPitted) {
                // Reference hasn't pitted: show pitted drivers from pit onwards
                if (driverBoostStopLap) {
                  if (
                    entry.lap > driverBoostStopLap ||
                    (entry.lap === driverBoostStopLap &&
                      entry.loop >= pitInLoop)
                  ) {
                    shouldInclude = true;
                  }
                  // Mark the pit stop
                  if (
                    entry.lap === driverBoostStopLap &&
                    entry.loop === pitInLoop
                  ) {
                    pitStopPoint = { x: xValue, y: yValue };
                  }
                }
              } else {
                // Reference has pitted: show all drivers up until their pit
                if (driverBoostStopLap) {
                  // Driver has pitted: show up until pit with marker
                  if (
                    entry.lap < driverBoostStopLap ||
                    (entry.lap === driverBoostStopLap &&
                      entry.loop <= pitInLoop)
                  ) {
                    shouldInclude = true;
                  }
                  // Mark the pit stop
                  if (
                    entry.lap === driverBoostStopLap &&
                    entry.loop === pitInLoop
                  ) {
                    pitStopPoint = { x: xValue, y: yValue };
                  }
                } else {
                  // Driver hasn't pitted yet: show all data
                  shouldInclude = true;
                }
              }

              if (shouldInclude) {
                dataPoints.push({ x: xValue, y: yValue });

                // Handle attack activations
                if (entry.inAttack) {
                  currentAttackActivation.push({ x: xValue, y: yValue });
                  wasInAttack = true;
                } else if (wasInAttack) {
                  // Transition from attack to not-attack: save activation
                  if (currentAttackActivation.length > 0) {
                    attackActivations.push([...currentAttackActivation]);
                    currentAttackActivation = [];
                  }
                  wasInAttack = false;
                }
              }
            }
          }

          // Don't forget to save the last activation if it ends while in attack
          if (currentAttackActivation.length > 0) {
            attackActivations.push(currentAttackActivation);
          }
        }

        // Add pit stop marker if exists
        if (pitStopPoint) {
          pitStopMarkers.push({
            label: `${driverName} Pit`,
            data: [pitStopPoint],
            backgroundColor: color,
            borderColor: color,
            pointStyle: "crossRot" as const,
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2,
            showLine: false as const,
            clip: false as const,
          });
        }

        // Add each attack activation as a separate trace
        attackActivations.forEach((activation) => {
          if (activation.length > 0) {
            attackTraces.push({
              label: `${driverName} Attack`,
              data: activation,
              backgroundColor: color,
              borderColor: color,
              borderWidth: 6,
              pointRadius: 0,
              pointHoverRadius: 0,
              fill: false,
            });
          }
        });

        // Add prediction line from last point to predicted gap at xMax
        if (dataPoints.length >= 1) {
          const lastPoint = dataPoints[dataPoints.length - 1];
          const driverPredictedGap = driverData?.predictedGapToLeader;
          const referencePredictedGap =
            participants[referenceDriver as numericalString]
              ?.predictedGapToLeader;

          if (
            driverPredictedGap != null &&
            referencePredictedGap != null &&
            lastPoint.x < calculatedXMax
          ) {
            const predictedDelta = -(
              driverPredictedGap - referencePredictedGap
            );
            const halfOpacityColor = colorWithOpacity(color, 0.5);

            predictionLines.push({
              label: `${driverName} Prediction`,
              data: [
                { x: lastPoint.x, y: lastPoint.y },
                {
                  x: calculatedXMax,
                  y: Math.round(predictedDelta * 100) / 100,
                },
              ],
              backgroundColor: halfOpacityColor,
              borderColor: halfOpacityColor,
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 0,
              fill: false,
              borderDash: borderDash,
            });
          }
        }

        return {
          label: `${driverName}`,
          data: dataPoints,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 2,
          pointRadius: 1,
          pointHoverRadius: 4,
          fill: false,
          borderDash: borderDash,
        };
      })
      .filter((dataset) => dataset.data.length > 0); // Only include drivers with data

    // Reference line at y=0
    const referenceDriverInfo = driverInfo[referenceDriver as numericalString];
    const referenceColor = referenceDriverInfo?.driverTeamColour || "white";
    const referenceLineStyle = referenceDriverInfo?.driverLineStyle || "solid";
    const referenceBorderDash = referenceLineStyle === "solid" ? [] : [10, 5];
    const referenceName = referenceDriverInfo?.driverName || referenceDriver;

    // Create reference line data points at y=0
    // If reference has pitted, only show from pit onwards with marker
    const referenceLineData: { x: number; y: number }[] = [];
    const referenceAttackActivations: { x: number; y: number }[][] = [];
    let currentRefAttackActivation: { x: number; y: number }[] = [];
    let refWasInAttack = false;
    let referencePitStopPoint: { x: number; y: number } | null = null;

    referenceHistory.forEach((entry) => {
      const xValue = getXValue(entry.lap, entry.loop);
      let shouldInclude = false;

      if (referenceHasPitted && referenceBoostStopLap) {
        // Reference has pitted: show from pit onwards
        if (
          entry.lap > referenceBoostStopLap ||
          (entry.lap === referenceBoostStopLap && entry.loop >= pitInLoop)
        ) {
          shouldInclude = true;
        }
        // Mark the pit stop
        if (entry.lap === referenceBoostStopLap && entry.loop === pitInLoop) {
          referencePitStopPoint = { x: xValue, y: 0 };
        }
      } else {
        // Reference hasn't pitted: show all data
        shouldInclude = true;
      }

      if (shouldInclude) {
        referenceLineData.push({ x: xValue, y: 0 });

        // Handle attack activations
        if (entry.inAttack) {
          currentRefAttackActivation.push({ x: xValue, y: 0 });
          refWasInAttack = true;
        } else if (refWasInAttack) {
          // Transition from attack to not-attack: save activation
          if (currentRefAttackActivation.length > 0) {
            referenceAttackActivations.push([...currentRefAttackActivation]);
            currentRefAttackActivation = [];
          }
          refWasInAttack = false;
        }
      }
    });

    // Don't forget to save the last activation if it ends while in attack
    if (currentRefAttackActivation.length > 0) {
      referenceAttackActivations.push(currentRefAttackActivation);
    }

    // Add reference pit stop marker if exists
    if (referencePitStopPoint) {
      pitStopMarkers.push({
        label: `${referenceName} Pit`,
        data: [referencePitStopPoint],
        backgroundColor: referenceColor,
        borderColor: referenceColor,
        pointStyle: "crossRot" as const,
        pointRadius: 5,
        pointHoverRadius: 7,
        borderWidth: 2,
        showLine: false as const,
        clip: false as const,
      });
    }

    // Add each reference attack activation as a separate trace
    referenceAttackActivations.forEach((activation) => {
      if (activation.length > 0) {
        attackTraces.push({
          label: `${referenceName} Attack`,
          data: activation,
          backgroundColor: referenceColor,
          borderColor: referenceColor,
          borderWidth: 8,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
        });
      }
    });

    // Add reference prediction line from last point to xMax at y=0
    if (referenceLineData.length >= 1) {
      const lastRefPoint = referenceLineData[referenceLineData.length - 1];
      if (lastRefPoint.x < calculatedXMax) {
        const refPredictionColor = colorWithOpacity(referenceColor, 0.5);
        predictionLines.push({
          label: `${referenceName} Prediction`,
          data: [
            { x: lastRefPoint.x, y: 0 },
            { x: calculatedXMax, y: 0 },
          ],
          backgroundColor: refPredictionColor,
          borderColor: refPredictionColor,
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          borderDash: referenceBorderDash,
        });
      }
    }

    const referenceLine = {
      label: `${referenceName} (Reference)`,
      data: referenceLineData,
      backgroundColor: referenceColor,
      borderColor: referenceColor,
      borderWidth: 3,
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
      borderDash: referenceBorderDash,
    };

    // Calculate min/max x values
    // xMin i minimum value from reference line data
    const referenceXValues = referenceLineData.map((pt) => pt.x);
    const xMin =
      referenceXValues.length > 0 ? Math.min(...referenceXValues) : 0;

    // Use the xMax calculated earlier for prediction lines
    const xMax = calculatedXMax;

    // Generate tick values for each loop position
    const tickValues: number[] = [];
    const tickLabelMap = new Map<number, string>();
    if (loopProportions && loopProportions.length > 0) {
      const minLap = Math.floor(xMin);
      const maxLap = Math.ceil(xMax);
      for (let lap = minLap; lap <= maxLap; lap++) {
        for (let loopIdx = 0; loopIdx < loopProportions.length; loopIdx++) {
          const tickValue =
            Math.round((lap + loopProportions[loopIdx]) * 1000) / 1000;
          if (tickValue >= xMin && tickValue <= xMax) {
            const loopName = loopNameMap[loopIdx] || `L${loopIdx}`;
            const displayLap = loopIdx === 0 ? lap - 1 : lap;
            tickValues.push(tickValue);
            tickLabelMap.set(tickValue, `${displayLap}.${loopName}`);
          }
        }
      }
    }

    return {
      datasets: [
        ...datasets,
        ...predictionLines,
        ...attackTraces,
        ...pitStopMarkers,
        referenceLine,
      ],
      xMin,
      xMax,
      tickValues,
      tickLabelMap,
      hasAlternateDrivers: datasets.length > 0,
    };
  }, [participants, referenceDriver, driverInfo, loopProportions, loopNameMap]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: `Opposite Strategy vs ${
          driverInfo[referenceDriver as keyof typeof driverInfo]?.driverName ||
          referenceDriver
        }`,
        color: "#000000ff",
        font: {
          size: 16,
          weight: "bold" as const,
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        min: chartData.xMin,
        max: chartData.xMax,
        title: {
          display: true,
          text: "Lap",
          color: "#000000ff",
        },
        ticks: {
          color: "#000000ff",
          autoSkip: true,
          maxRotation: 90,
          minRotation: 45,
          callback: function (value: string | number) {
            if (!chartData.hasAlternateDrivers) return "";
            const numValue =
              typeof value === "string" ? parseFloat(value) : value;
            const roundedValue = Math.round(numValue * 1000) / 1000;
            return chartData.tickLabelMap.get(roundedValue) || "";
          },
        },
        afterBuildTicks: (axis: { ticks: { value: number }[] }) => {
          if (chartData.tickValues && chartData.tickValues.length > 0) {
            axis.ticks = chartData.tickValues.map((v) => ({ value: v }));
          }
        },
        grid: {
          color: chartData.hasAlternateDrivers
            ? "rgba(0, 0, 0, 0.1)"
            : "transparent",
        },
      },
      y: {
        title: {
          display: true,

          text: "Gap (s)",
          color: "#000000ff",
        },
        ticks: {
          color: "#000000ff",
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        min: -10,
        max: 10,
      },
    },
    interaction: {
      mode: "point" as const,
      axis: "x" as const,
      intersect: false,
    },
    elements: {
      point: {
        backgroundColor: "#000000ff",
        borderWidth: 2,
      },
    },
  };

  return (
    <div className={styles.container}>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

export default OppositePitStrategyGraph;
